import { useTranslation } from "next-i18next";
import { NotifierFactory } from "@shared/services/api/abstracts/notifiers/NotifierFactory";
import { InputProps } from "@ui-kit/form/Input/Input.web";
import { useCallback } from "react";
import { ButtonProps } from "../../Button/Button.web";

const notifier = NotifierFactory.createNotifier();

/**
 * Combine Button and Input props but omit conflicting props.
 * variant is used for buttons, not inputs.
 */
export type IncrementerProps = Omit<InputProps, "onChange" | "variant"> &
  Omit<ButtonProps, "onChange" | "data-testid"> & {
    value: number;
    onChange(value: number): void;
    incrementerVariant?: "input" | "default";
    max?: number | null;
  };

export function useFormIncrementor({
  value,
  onChange,
  min,
  max,
  isDisabled,
}: IncrementerProps) {
  const { t } = useTranslation("common");
  const amountRequested = value;
  const hasError =
    amountRequested === 0
      ? false
      : (min !== undefined && amountRequested < min) ||
        (max !== undefined && amountRequested > max);

  // runs from pressing the + button
  // if the new value is below the minimum purchase limit, set the value to the minimum limit
  // example: if the minimum purchase limit is 5 and current amount is 0, the amount will jump from 0 to 5
  const onIncrement = useCallback(() => {
    const newState = Math.max(amountRequested + 1, Number(min) || 0);

    onChange(newState);
  }, [min, amountRequested, onChange]);

  // runs from pressing the - button
  // if the new value is below the minimum purchase limit, set the value to 0
  // example: if the minimum purchase limit is 5 and current amount is 5, the amount will jump from 5 to 0
  const onDecrement = useCallback(() => {
    const newState = min && amountRequested - 1 < min ? 0 : amountRequested - 1;
    onChange(newState);
  }, [min, amountRequested, onChange]);

  // prevents the user from pressing the up and down arrow keys to change the input box values
  // this is restricted because the updated input value is not available from key presses
  // so non-zero minimum limits can't be handled properly
  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
      }
    },
    []
  );

  // runs from changing the input value
  // automatically sets the value to the minimum limit or the maximum limit, if the input value is outside of the valid range
  const onInput = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const targetValue = Number(event.target.value);

      onChange(targetValue);

      if (targetValue > Number(max)) {
        notifier.error(
          t("The maximum quantity is {{ count }}", { count: max })
        );
      }
    },
    [max, min, onChange, t]
  );

  // disable text input if the max value is zero
  // for tickets, the max is defined by the value of $countOptions returned from the backend
  const isDisabledInput = isDisabled || max === 0;

  // disable min button if user tries to select less than zero items
  const isDisabledMin = isDisabled || amountRequested <= 0;

  // disable max button if user tries to select over the max number of items
  const isDisabledMax = Boolean(isDisabled || (max && amountRequested >= max));

  return {
    hasError,
    amountRequested,
    isDisabledInput,
    isDisabledMin,
    isDisabledMax,
    onInput,
    onDecrement,
    onIncrement,
    onKeyDown,
  };
}
